import { z } from 'zod'
import type { ValueKind } from '#src/observation-logic/value.ts'

// What a condition reads: a context variable (`age`), or an observation in
// the note by its code. `nearest` reads the closest preceding one with the
// code; `any` the latest one answered.
export interface VariableOperand {
  source: 'variable'
  id: string
}

export interface ObservationOperand {
  source: 'observation'
  code: string
  system?: string
  scope?: 'nearest' | 'any'
}

export type Operand = VariableOperand | ObservationOperand

export interface NumberLiteral {
  kind: 'number'
  value: number
  unit?: string
}

export interface DateLiteral {
  kind: 'date'
  value: string
}

export interface StringLiteral {
  kind: 'string'
  value: string
}

export interface BooleanLiteral {
  kind: 'boolean'
  value: boolean
}

export interface CodingLiteral {
  kind: 'coding'
  code: string
  system?: string
  // The answer's display when it was picked, for wording the condition
  // without a lookup. Not compared.
  display?: string
}

export type ConditionLiteral =
  | NumberLiteral
  | DateLiteral
  | StringLiteral
  | BooleanLiteral
  | CodingLiteral

export const OPERATORS = ['=', '!=', '<', '<=', '>', '>=', 'in'] as const
export type Operator = (typeof OPERATORS)[number]

// The comparisons each kind of value takes. `in` compares with a list.
export const OPERATORS_BY_KIND = {
  number: ['=', '!=', '<', '<=', '>', '>=', 'in'],
  date: ['=', '!=', '<', '<=', '>', '>='],
  string: ['=', '!=', 'in'],
  boolean: ['=', '!='],
  coding: ['=', '!=', 'in'],
} as const satisfies Record<ValueKind, readonly Operator[]>

export interface AllCondition {
  type: 'all'
  conditions: Condition[]
}

export interface AnyCondition {
  type: 'any'
  conditions: Condition[]
}

export interface NotCondition {
  type: 'not'
  condition: Condition
}

// `is answered` (answered: true) or `is not answered` (answered: false).
export interface AnsweredCondition {
  type: 'answered'
  operand: Operand
  answered: boolean
}

// `in` takes a list of literals of one kind; every other operator one
// literal.
export interface CompareCondition {
  type: 'compare'
  operand: Operand
  op: Operator
  value: ConditionLiteral | ConditionLiteral[]
}

export type Condition =
  | AllCondition
  | AnyCondition
  | NotCondition
  | AnsweredCondition
  | CompareCondition

export const operandSchema: z.ZodType<Operand> = z.discriminatedUnion(
  'source',
  [
    z.object({ source: z.literal('variable'), id: z.string().min(1) }),
    z.object({
      source: z.literal('observation'),
      code: z.string().min(1),
      system: z.string().min(1).optional(),
      scope: z.enum(['nearest', 'any']).optional(),
    }),
  ]
)

export const conditionLiteralSchema: z.ZodType<ConditionLiteral> =
  z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('number'),
      value: z.number().finite(),
      unit: z.string().min(1).optional(),
    }),
    z.object({
      kind: z.literal('date'),
      value: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2})?)?$/),
    }),
    z.object({ kind: z.literal('string'), value: z.string() }),
    z.object({ kind: z.literal('boolean'), value: z.boolean() }),
    z.object({
      kind: z.literal('coding'),
      code: z.string().min(1),
      system: z.string().min(1).optional(),
      display: z.string().min(1).optional(),
    }),
  ])

// Whether an operator applies to a literal of this kind.
export function operatorAllowed(kind: ValueKind, op: Operator): boolean {
  const allowed: readonly Operator[] = OPERATORS_BY_KIND[kind]
  return allowed.includes(op)
}

const compareSchema = z
  .object({
    type: z.literal('compare'),
    operand: operandSchema,
    op: z.enum(OPERATORS),
    value: z.union([
      conditionLiteralSchema,
      z.array(conditionLiteralSchema).min(1),
    ]),
  })
  .superRefine((condition, context) => {
    const values = Array.isArray(condition.value)
      ? condition.value
      : [condition.value]
    if (Array.isArray(condition.value) !== (condition.op === 'in')) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: '`in` takes a list of values; every other operator one value.',
        path: ['value'],
      })
    }
    const kinds = new Set(values.map((value) => value.kind))
    if (kinds.size > 1) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A list of values must all be of one kind.',
        path: ['value'],
      })
    }
    for (const kind of kinds) {
      if (!operatorAllowed(kind, condition.op)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `\`${condition.op}\` does not apply to a ${kind}.`,
          path: ['op'],
        })
      }
    }
  })

export const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    z.object({
      type: z.literal('all'),
      conditions: z.array(conditionSchema),
    }),
    z.object({
      type: z.literal('any'),
      conditions: z.array(conditionSchema),
    }),
    z.object({ type: z.literal('not'), condition: conditionSchema }),
    z.object({
      type: z.literal('answered'),
      operand: operandSchema,
      answered: z.boolean(),
    }),
    compareSchema,
  ])
)
